import { FormEvent, useEffect, useState } from 'react'
import { newsletterAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'

/**
 * On a creator's public page: sign up for their newsletter. Shown only when
 * it's taking sign-ups. The address is added once the person follows the
 * link emailed to them (double opt-in).
 */
const NewsletterSignup = ({ handle }: { handle: string }) => {
  const [accepting, setAccepting] = useState(false)
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let stopped = false
    // Optional: no form when it can't be checked.
    Promise.resolve()
      .then(() => newsletterAPI.signupStatus(handle))
      .then((s) => !stopped && setAccepting(s.accepting))
      .catch(() => undefined)
    return () => {
      stopped = true
    }
  }, [handle])

  if (!accepting) return null

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setState('sending')
    setError(null)
    try {
      await newsletterAPI.signup(handle, email.trim())
      setState('sent')
    } catch (err) {
      setState('idle')
      setError(apiErrorMessage(err, "Couldn't sign you up. Try again."))
    }
  }

  return (
    <section aria-label="Newsletter" className="rounded-md border border-gray-700 p-4" data-testid="newsletter-signup">
      <h2 className="text-sm font-semibold text-gray-100">Get @{handle}&rsquo;s newsletter</h2>
      {state === 'sent' ? (
        <p role="status" className="mt-2 text-sm text-gray-300">
          Check your inbox for a link to confirm. Nothing is sent until you do.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-2 flex flex-wrap gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Your email address"
            placeholder="you@example.com"
            className="min-w-0 flex-1 rounded-md border border-gray-600 bg-gray-900 px-2 py-1.5 text-sm text-gray-100"
          />
          <button
            type="submit"
            disabled={state === 'sending'}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {state === 'sending' ? 'Sending…' : 'Sign up'}
          </button>
        </form>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-500">
          {error}
        </p>
      )}
      <p className="mt-2 text-xs text-gray-500">Every issue has a link to unsubscribe.</p>
    </section>
  )
}

export default NewsletterSignup
